/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100900 マスタデータEXCEL取込
 */

import { Box } from "@mui/material";
import { catchRestError } from "clients/rest";
import {
  B100900Request,
  B100900Shohin,
  B100900Toritanka,
  B100900Tanka,
} from "clients/rest";
import {
  msgState,
  SFileField,
  SFrame,
  SLabel,
  SNormalButton,
  SText,
  STextArea,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import { labels } from "messages/labels";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { outputText } from "services";
import { focusState, useFocus } from "states";

export type GamenInputId = {
  gamenId: string;
};
// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  dataType: string;
  gamenId: string;
  filedropArea: File;
  outputText: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  dataType: "",
  gamenId: "",
  filedropArea: undefined,
  outputText: "",
};

const focusId = "B100900";

// 画面ID
// 商品マスタ
const gamenIdShohin = "100110";
// 単価マスタ
const gamenIdTanka = "100140";
// 取引先別単価マスタ
const gamenIdToritanka = "100150";

// データ種別
// 商品マスタ
const dataTypeShohin = "商品マスタ";
// 単価マスタ
const dataTypeTanka = "単価マスタ";
// 取引先別単価マスタ
const dataTypeToritanka = "取引先別単価マスタ";

// B100900 マスタデータEXCEL取込
export const B100900 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 実行ボタン活性制御
  const disabledExecuteButton = methods.watch("filedropArea") === undefined;
  // 結果レポート出力ボタン活性制御
  const [isDisabledReportButton, setIsDesabledReportButton] = useState(true);
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 遷移元データ取得用
  const location = useLocation();
  const gamenInputId = location.state as GamenInputId;

  // 初期表示イベント
  useEffect(() => {
    if (!gamenInputId) {
      return;
    }

    methods.setValue("gamenId", gamenInputId.gamenId);

    // 商品マスタ
    if (gamenInputId.gamenId === gamenIdShohin) {
      methods.setValue("dataType", dataTypeShohin);
    }
    // 単価マスタ
    else if (gamenInputId.gamenId === gamenIdTanka) {
      methods.setValue("dataType", dataTypeTanka);
    }
    // 取引先別単価マスタ
    else if (gamenInputId.gamenId === gamenIdToritanka) {
      methods.setValue("dataType", dataTypeToritanka);
    }

    // 初回フォーカス先
    focus(1);
  }, []);

  // 実行ボタン押下イベント
  const handleExecute = () => {
    const fields = methods.getValues();
    methods.setValue("outputText", getClientMessage("B100900_001"));

    // 商品マスタ取込
    if (fields.gamenId === gamenIdShohin) {
      handleImpShohin(fields);
      // 単価マスタ取込
    } else if (fields.gamenId === gamenIdTanka) {
      handleImpTanka(fields);
      // 取引先別単価マスタ取込
    } else if (fields.gamenId === gamenIdToritanka) {
      handleImpToritanka(fields);
    }

    setIsDesabledReportButton(false);
  };

  // 商品マスタ取込
  const handleImpShohin = (fields: Fields) => {
    const param: B100900Request = {
      excelFileData: fields.filedropArea!,
    };

    B100900Shohin(param)
      .then((value) => {
        // 結果を出力
        methods.setValue("outputText", value.data.shohin);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 単価マスタ取込
  const handleImpTanka = (fields: Fields) => {
    const param: B100900Request = {
      excelFileData: fields.filedropArea!,
    };

    B100900Tanka(param)
      .then((value) => {
        // 結果を出力
        methods.setValue("outputText", value.data.tanka);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 取引先別単価マスタ取込
  const handleImpToritanka = (fields: Fields) => {
    const param: B100900Request = {
      excelFileData: fields.filedropArea!,
    };

    B100900Toritanka(param)
      .then((value) => {
        // 結果を出力
        methods.setValue("outputText", value.data.toritanka);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 結果レポート出力
  const handleClickReportButton = () => {
    const text = methods.getValues().outputText;
    if (typeof text === "undefined") return;
    outputText(text, "マスタデータEXCEL取込結果.txt");
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="マスタデータEXCEL取込"
        width={1275}
        gridTemplateColumns="7rem 10rem 13rem "
      >
        <SLabel>取込データ種別</SLabel>
        <STextField label="取込データ種別" name="dataType" disabled />
        <Box />
      </SFrame>

      <SFrame gridColumn="span 3">
        <SFileField
          name="filedropArea"
          label="取り込むマスタデータEXCELファイル"
          type="excel"
          height="7rem"
        />

        <SNormalButton
          label={labels.buttons.execute}
          onClick={handleExecute}
          disabled={disabledExecuteButton}
          align="right"
          {...register(1)}
        />
      </SFrame>

      <SFrame>
        <SText align="left">マスタデータEXCEL取込結果</SText>

        <STextArea name="outputText" height="30rem" disabled />

        <SNormalButton
          label={labels.buttons.resultReport}
          onClick={handleClickReportButton}
          disabled={isDisabledReportButton}
          align="right"
          {...register(2)}
        />
      </SFrame>
    </FormProvider>
  );
};
